import { useEffect, useRef, useState } from "react";
import { createChart, CrosshairMode } from "lightweight-charts";
import { api, IST_OFFSET, num } from "@/lib/api";

const opts = (h) => ({
  autoSize: true, height: h,
  localization: { locale: "en-IN" },
  layout: { background: { color: "#0B1F3A" }, textColor: "#64748B", fontFamily: "JetBrains Mono", fontSize: 10 },
  grid: { vertLines: { color: "#141A23" }, horzLines: { color: "#141A23" } },
  rightPriceScale: { borderColor: "#163056", minimumWidth: 72 },
  timeScale: { borderColor: "#163056", timeVisible: true, secondsVisible: false },
  crosshair: { mode: CrosshairMode.Normal },
});
const line = (color, w = 1) => ({ color, lineWidth: w, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });

export const OVERLAYS = [
  { key: "ema9", label: "EMA 9", color: "#22A34A" },
  { key: "ema21", label: "EMA 21", color: "#FFAB00" },
  { key: "sma50", label: "SMA 50", color: "#E040FB" },
  { key: "sma200", label: "SMA 200", color: "#F472B6" },
  { key: "bb", label: "BB 20", color: "#38BDF8" },
  { key: "rsi", label: "RSI 14", color: "#D500F9" },
  { key: "macd", label: "MACD", color: "#2979FF" },
];

const toLine = (c, arr) => c.map((k, i) => (arr[i] == null ? { time: k.time } : { time: k.time, value: arr[i] }));

export const PriceChart = ({ symbol, tf, ind, markers, limit = 500, height = 420, onLast }) => {
  const mainEl = useRef(), rsiEl = useRef(), macdEl = useRef();
  const s = useRef({});
  const markersRef = useRef(markers);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const r = s.current;
    const main = createChart(mainEl.current, opts(height));
    const rsi = createChart(rsiEl.current, opts(110));
    const macd = createChart(macdEl.current, opts(110));
    r.charts = [main, rsi, macd];
    r.candle = main.addCandlestickSeries({ upColor: "#00E676", downColor: "#FF3B30", borderVisible: false, wickUpColor: "#00E676", wickDownColor: "#FF3B30" });
    r.vol = main.addHistogramSeries({ priceScaleId: "vol", priceFormat: { type: "volume" }, lastValueVisible: false, priceLineVisible: false });
    main.priceScale("vol").applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });
    r.ema9 = main.addLineSeries(line("#22A34A", 1.5));
    r.ema21 = main.addLineSeries(line("#FFAB00", 1.5));
    r.sma50 = main.addLineSeries(line("#E040FB"));
    r.sma200 = main.addLineSeries(line("#F472B6"));
    r.bbU = main.addLineSeries(line("#38BDF8"));
    r.bbM = main.addLineSeries({ ...line("#38BDF8"), lineStyle: 2 });
    r.bbL = main.addLineSeries(line("#38BDF8"));
    r.rsi = rsi.addLineSeries(line("#D500F9", 1.5));
    [70, 30].forEach((p) => r.rsi.createPriceLine({ price: p, color: "#334155", lineStyle: 2, lineWidth: 1, axisLabelVisible: true }));
    r.hist = macd.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false });
    r.macd = macd.addLineSeries(line("#2979FF", 1.5));
    r.sig = macd.addLineSeries(line("#FF9100", 1.5));
    main.timeScale().subscribeVisibleLogicalRangeChange((rg) => {
      if (!rg) return;
      rsi.timeScale().setVisibleLogicalRange(rg);
      macd.timeScale().setVisibleLogicalRange(rg);
    });
    setReady(true);
    return () => { r.charts.forEach((c) => c.remove()); s.current = {}; };
  }, [height]);

  const applyMarkers = (candles) => {
    const r = s.current;
    if (!r.candle || !candles.length) return;
    const first = candles[0].time;
    const m = (markersRef.current || [])
      .map((x) => ({ ...x, time: x.time + IST_OFFSET }))
      .filter((x) => x.time >= first)
      .sort((a, b) => a.time - b.time)
      .map((x) => x.side === "BUY"
        ? { time: x.time, position: "belowBar", color: "#00E676", shape: "arrowUp", text: "B" }
        : { time: x.time, position: "aboveBar", color: "#FF3B30", shape: "arrowDown", text: "S" });
    r.candle.setMarkers(m);
  };

  useEffect(() => {
    if (!ready) return;
    let alive = true, first = true;
    const load = async () => {
      const { data } = await api.get("/candles", { params: { symbol, tf, limit } });
      const r = s.current;
      if (!alive || !r.candle) return;
      const c = data.candles.map((k) => ({ ...k, time: k.time + IST_OFFSET }));
      const I = data.indicators;
      r.candle.setData(c);
      r.vol.setData(c.map((k) => ({ time: k.time, value: k.volume, color: k.close >= k.open ? "rgba(0,230,118,0.25)" : "rgba(255,59,48,0.25)" })));
      r.ema9.setData(toLine(c, I.ema9)); r.ema21.setData(toLine(c, I.ema21));
      r.sma50.setData(toLine(c, I.sma50)); r.sma200.setData(toLine(c, I.sma200));
      r.bbU.setData(toLine(c, I.bb_upper)); r.bbM.setData(toLine(c, I.bb_mid)); r.bbL.setData(toLine(c, I.bb_lower));
      r.rsi.setData(toLine(c, I.rsi));
      r.macd.setData(toLine(c, I.macd)); r.sig.setData(toLine(c, I.macd_signal));
      r.hist.setData(c.map((k, i) => (I.macd_hist[i] == null ? { time: k.time } : { time: k.time, value: I.macd_hist[i], color: I.macd_hist[i] >= 0 ? "rgba(0,230,118,0.5)" : "rgba(255,59,48,0.5)" })));
      applyMarkers(c);
      if (first) {
        first = false;
        r.charts[0].timeScale().setVisibleLogicalRange({ from: Math.max(0, c.length - 150), to: c.length + 5 });
      }
      onLast?.(data.candles[data.candles.length - 1], data.candles[data.candles.length - 2]);
    };
    load().catch(() => {});
    const id = setInterval(() => load().catch(() => {}), 3000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, symbol, tf, limit]);

  useEffect(() => {
    markersRef.current = markers;
  }, [markers]);

  useEffect(() => {
    const r = s.current;
    if (!r.candle) return;
    ["ema9", "ema21", "sma50", "sma200"].forEach((k) => r[k].applyOptions({ visible: !!ind[k] }));
    [r.bbU, r.bbM, r.bbL].forEach((x) => x.applyOptions({ visible: !!ind.bb }));
  }, [ind, ready]);

  return (
    <div className="flex flex-col" data-testid="price-chart">
      <div ref={mainEl} style={{ height }} />
      <div className={ind.rsi ? "border-t border-[#163056] relative" : "hidden"} data-testid="rsi-pane">
        <span className="absolute left-2 top-1 z-10 text-[10px] font-mono text-[#D500F9]">RSI 14</span>
        <div ref={rsiEl} style={{ height: 110 }} />
      </div>
      <div className={ind.macd ? "border-t border-[#163056] relative" : "hidden"} data-testid="macd-pane">
        <span className="absolute left-2 top-1 z-10 text-[10px] font-mono text-[#2979FF]">MACD 12 26 9</span>
        <div ref={macdEl} style={{ height: 110 }} />
      </div>
    </div>
  );
};

export const OhlcLegend = ({ last }) =>
  last ? (
    <div className="flex gap-3 font-mono text-[11px] text-slate-500" data-testid="ohlc-legend">
      {["open", "high", "low", "close"].map((k) => (
        <span key={k}>{k[0].toUpperCase()} <span className="text-slate-200">{num(last[k])}</span></span>
      ))}
    </div>
  ) : null;
